<template>
<div>
  <v-row>
    <page-title value="午睡チェック一覧"></page-title>
    <v-spacer />
    <v-btn color="primary" variant="flat" to="/nap/create">
      <v-icon start>mdi-plus</v-icon>
      午睡チェック作成
    </v-btn>
  </v-row>

  <v-card variant="flat" class="mt-6 pa-2" color="grey-lighten-5">
    <v-container fluid>
      <v-row align="center">
        <v-col cols="2">
          <h5 style="margin-bottom: 16px;">日付で絞り込み</h5>
          <date-picker v-model="from" label="開始日"></date-picker>
        </v-col>
        <v-col cols="2">
          <div style="margin-bottom: 16px;"><br></div>
          <date-picker v-model="to" label="終了日"></date-picker>
        </v-col>
        <v-col cols="8">
        </v-col>
      </v-row>
      <v-row align="center">
        <v-col cols="4">
          <h5>クラス</h5>
          <class-picker
            search
            :classes="classes"
            v-model="classId"
          ></class-picker>
        </v-col>
        <v-col cols="4">
          <h5>年齢</h5>
          <age-picker v-model="age"></age-picker>
        </v-col>
        <v-col cols="4">
          <v-btn
            color="primary"
            variant="flat"
            class="mt-4"
            @click="search"
            :disabled="isSearching"
          >検索</v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-card>

  <div>
    <v-progress-linear
      v-if="isSearching"
      indeterminate
      color="primary"
      class="my-8"
    ></v-progress-linear>
  </div>

  <div class="pt-10">
    <v-table>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">
              日付
            </th>
            <th class="text-left">
              対象
            </th>
            <th class="text-left">
              間隔(分)
            </th>
            <th class="text-left">
              人数
            </th>
            <th class="text-left">
              状態
            </th>
            <th class="text-left">
              午睡終了時刻
            </th>
            <th class="text-left">
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="n in napList"
            :key="n.napId"
          >
            <td>
              {{ n.date }}
            </td>
            <td>
              {{ formatTarget(n) }}
            </td>
            <td>
              {{ formatInterval(n.interval) }}
            </td>
            <td>
              {{ n.childCount }}
            </td>
            <td>
              {{ formartStatus(n.status) }}
            </td>
            <td>
              {{ formartDate(n.finishedAt) }}
            </td>
            <td>
              <v-btn
                variant="flat"
                color="primary"
                class="ma-2"
                :to="`/nap/${n.napId}/detail`"
              >詳細</v-btn>
              <v-btn
                v-if="n.status === 'inProgress'"
                variant="outlined"
                color="primary"
                class="ma-2"
                :to="`/nap/${n.napId}/sensor`"
              >センサー画面</v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-table>
  </div>
</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import PageTitle from '@/components/atoms/PageTitle.vue'
import ClassPicker from '@/components/atoms/ClassPicker.vue'
import AgePicker from '@/components/atoms/AgePicker.vue'
import DatePicker from '@/components/atoms/DatePicker.vue'

import LocalDataService from '@/service/LocalDataService'
import { classModule } from '@/store/dataModules/classModule'
import { NurseryClassResponse, NurseryNapResponse } from 'chaild-api/lib'

import { napListModule } from '@/store/dataModules/napListModule';
import dayjs from 'dayjs'

interface DataType {
  isSearching: boolean;
}

export default defineComponent({
  name: 'NapList',

  // todo: 午睡チェックの右上おしたら、新規作成を挟む（そこでテンプレートも読み込める）
  components: {
    PageTitle,
    ClassPicker,
    AgePicker,
    DatePicker,
  },

  data: (): DataType => {
    return {
      isSearching: false,
    };
  },

  computed: {
    classes(): NurseryClassResponse[] {
      return classModule.classes;
    },
    napList(): NurseryNapResponse[] {
      return napListModule.naps;
    },
    from: {
      get(): string | null {
        return napListModule.from;
      },
      set(date: string) {
        napListModule.setFrom(date);
      },
    },
    to: {
      get(): string | null {
        return napListModule.to;
      },
      set(date: string) {
        napListModule.setTo(date);
      },
    },
    classId: {
      get(): number | null {
        return napListModule.classId;
      },
      set(value: number | null) {
        napListModule.setClassId(value);
      }
    },
    age: {
      get(): number | null {
        return napListModule.age;
      },
      set(value: number | null) {
        napListModule.setAge(value);
      }
    },
  },

  methods: {
    async search() {
      this.isSearching = true;
      if (this.from) sessionStorage.setItem('napFrom', this.from); else sessionStorage.setItem('napFrom', '');
      if (this.to) sessionStorage.setItem('napTo', this.to); else sessionStorage.setItem('napTo', '');
      if (this.classId) sessionStorage.setItem('napClassId', String(this.classId)); else sessionStorage.setItem('napClassId', '');
      if (this.age) sessionStorage.setItem('napAge', String(this.age)); else sessionStorage.setItem('napAge', '');

      // classIdがallの時にエラーが出る対策
      if (this.classId && isNaN(this.classId)) this.classId = null;

      await napListModule.listNaps();
      this.isSearching = false;
    },
    formatTarget(nap: NurseryNapResponse) {
      const classLabel = nap.class.all ?
        'すべての年齢' :
        nap.class.classes.map((c) => c.className).join(', ');
      const ageLabel = nap.age.all ?
        'すべての年齢' :
        nap.age.ages.map((a) => a === 5 ? '5歳以上' : `${a}歳`).join(', ');
      return `${classLabel} / ${ageLabel}`;
    },
    formatInterval(value: number) {
      switch (value) {
        case 0:
          return '5分';
        case 1:
          return '10分';
        case 2:
          return '15分';
        case 3:
          return '30分';
        case 4:
          return '60分';
        default:
          return '';
      }
    },
    formartStatus(value: string) {
      switch (value) {
        case 'inProgress':
          return '午睡中';
        case 'finished':
          return '午睡終了';
        case 'applied':
          return '申請済み';
        default:
          return '';
      }
    },
    formartDate(value: string | null | undefined) {
      if (value) {
        return dayjs(value).format('MM/DD HH:mm');
      }
      return '';
    },
    openNewTab(value: string) {
      window.open(value, '_blank');
    },
  },

  mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      classModule.listClass({
        nurseryId: nurseryId,
      });
      const napFrom = sessionStorage.getItem('napFrom')
      const napTo = sessionStorage.getItem('napTo')
      const napClassId = sessionStorage.getItem('napClassId')
      const napAge = sessionStorage.getItem('napAge')

      if (napFrom) this.from = napFrom;
      if (napTo) this.to = napTo;
      if (napClassId != '') {
        this.classId = Number(napClassId);
      } else {
        this.classId = null;
      }
      if (napAge) this.age = Number(napAge);

      this.search();
    }
  }
})
</script>